/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ViewportScroller} from '@angular/common';
import {ChangeDetectionStrategy, Component, HostListener, OnInit,} from '@angular/core';
import {FormControl} from '@angular/forms';
import {NavigationEnd} from '@angular/router';
import {NbMenuService, NbWindowService} from '@nebular/theme';
import {DamEventService, FileService, LazyCatalog, StoreService} from 'compob2b-libs';
import {FileUploader} from 'ng2-file-upload';
import {from, Observable, Subject, Subscription} from 'rxjs';
import {concatMap, filter, finalize, map, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {CrudConfig} from '../../../admin-shared/interfaces/crud.config';
import {FormConfigService} from '../../../admin-shared/services/form-config.service';
import {CatalogLazyComponent} from '../../pim/catalog/lazy-tree/catalog-lazy.component';
import {DamExternalUploadComponent} from '../dam-external-upload/dam-external-upload.component';
import {DAM_ITEM_FORM} from '../dam-forms.config';
import {DamShareComponent} from '../dam-share/dam-share.component';
import {DamCatalogService} from '../services/dam-catalog.service';

interface DocumentFile {
  enabled: boolean;
  header: string;
  fileInput: File;
}

@Component({
  selector: 'app-dam-catalog-tree',
  templateUrl: './dam-catalog-lazy.component.html',
  styleUrls: ['./dam-catalog-lazy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DamCatalogLazyComponent extends CatalogLazyComponent implements OnInit {
  hasDropZoneOver: boolean;
  itemPage = false;
  folder = '';
  style: object = {};
  uploadURI: string;
  uploader: FileUploader;
  isUploading = false;
  menuId: number;
  keyword = '';
  readonly formControl: FormControl;
  config: CrudConfig = {
    uri: '/dam/files',
    uriDelete: '/dam-menu',
    columns: DAM_ITEM_FORM,
    defaultSortColumn: 'header',
    defaultSortDirection: 'asc',
    title: translations.catalog
  };
  scrollPos: [number, number] = [0, 0];
  subscription: Subscription;

  readonly map = new Map<LazyCatalog, boolean>();
  readonly uploading = new Subject<boolean>();

  canUpload = false;
  damUploadFinish = new Observable<boolean>();

  constructor(protected readonly formsConfig: FormConfigService,
              protected readonly windowService: NbWindowService,
              readonly catalogService: DamCatalogService,
              protected readonly fileService: FileService,
              protected readonly menuSerivice: NbMenuService,
              protected scroller: ViewportScroller,
              protected readonly storeService: StoreService,
              protected readonly damEventService: DamEventService
  ) {
    super(formsConfig, windowService, catalogService, fileService);
  }

  ngOnInit() {
    this.damUploadFinish = this.damEventService.getDamUploadFinish();
    this.catalogId = Number(this.route.snapshot.children[0].params.catalog_id) || 0;

    if (this.storeService.getCustomer()['roles'].some(obj => (obj.name === 'ROLE_ADMIN' || obj.name === 'ROLE_SUPER_ADMIN'))) {
      this.canUpload = true;
    }

    this.initCatalogTree();

    this.itemPage = this.router.url.indexOf('/item/') > -1;
    this.router.events.pipe(
      takeUntil(this.unsubscribe),
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      this.itemPage = event.url.indexOf('/item/') > -1;
      this.scroller.scrollToPosition(this.scrollPos);
    });

    this.route.queryParamMap.pipe(
      tap(params => this.keyword = params.get('keyword')),
      tap(params => this.folder = params.get('folder')),
      tap(params => {
        if (params.get('catalog_id') === null || params.get('catalog_id') === undefined) {
          this.menuId = 0;
          this.catalogId = 0;
        } else {
          this.menuId = Number(params.get('catalog_id'));
          this.catalogId = Number(params.get('catalog_id'));
        }

      }),
      tap(_ => this.cdr.detectChanges()),
      takeUntil(this.unsubscribe),
    ).subscribe();

    this.uploader = new FileUploader({
      url: null,
      disableMultipart: false,
      formatDataFunctionIsAsync: false,
      isHTML5: true,
      removeAfterUpload: false,
      autoUpload: false,
      maxFileSize: 350 * 1024 * 1024,
    });

    super.setConfig(this.config);
    this.configure();

    this.menuSerivice.onItemClick().pipe(takeUntil(this.unsubscribe),
      tap(element => {
        if (element.item.link === '/dam') {
          this.catalogId = null;
          this.data1.forEach(item => {
            const index = this.data1.indexOf(item);
            item.expanded = false;
            delete item.children;
            this.data1[index] = item;
          });
          this.cdr.detectChanges();
        }
      })
    )
      .subscribe();

    this.route.queryParamMap.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(params => {
      this.clearMenu(params);
    });
  }

  uploadFile(uploadingFile: File): Observable<any> {
    this.uploadURI = '/dam/upload/' + this.menuId;
    return this.compoApiService.postForm<DocumentFile, string>(
      this.uploadURI,
      {
        enabled: true,
        header: uploadingFile.name,
        fileInput: uploadingFile
      }
    );
  }

  upload(): void {
    this.isUploading = true;
    this.uploading.next(true);
    if (this.uploader.queue.length) {
      this.subscription = from(this.uploader.queue)
        .pipe(
          map(uploadingFile => uploadingFile._file),
          concatMap(uploadingFile => this.uploadFile(uploadingFile)),
          tap(response => {
            if (!response.success) {
              this.warningToast('Данный файл уже загружен раннее');
            }
          }),
          finalize(() => this.finishUpload()),
          takeUntil(this.unsubscribe)
        ).subscribe();
      this.hasDropZoneOver = false;
    }
  }

  finishUpload(): void {
    console.log('finishUpload1');
    this.uploader.clearQueue();
    this.subscription.unsubscribe();
    this.isUploading = false;
    this.uploading.next(false);
    this.damEventService.nextDamUploadFinish(true);
    this.cdr.detectChanges();
  }

  clearMenu(params) {
    if (params.get('catalog_id') === null) {
      this.data1.forEach(item => {
        const index = this.data1.indexOf(item);
        item.expanded = false;
        delete item.children;
        this.data1[index] = item;
      });
      this.cdr.detectChanges();
    } else {
      this.catalogId = params.get('catalog_id');

      const clickItem = this.getParent(this.data1, Number(params.get('catalog_id')));
      if (clickItem === null) {
        this.initCatalogTree();
      }
      this.cdr.detectChanges();
    }
  }

  createExternalLink(id: number) {
    this.windowService.open(DamExternalUploadComponent, {
      context: {
        damCatalog: id,
      }
    });
  }

  navigateToId(id: number) {
    console.warn(id);
    if (!id) {
      this.router.navigate(['cabinet', 'dam']);
    } else {
      this.router.navigate(['cabinet', 'dam'], {queryParams: {catalog_id: this.catalogId}});
    }
  }

  @HostListener('keydown.enter')
  searchByKeyword() {
    if (this.keyword === '') {
      this.keyword = null;
    }

    this.onNavigate({keyword: this.keyword, page: null});
  }

  fileOver(e: any): void {
    this.hasDropZoneOver = e;
  }

  shareDamItems(damCatalogId: number): void {
    this.windowService.open(DamShareComponent, {
      context: {
        damItems: [],
        damCatalog: damCatalogId,
        shareType: 1
      }
    });
  }

  downloadDamCatalog(damCatalogId: number): void {
    this.fileService.downloadUrl('/dam-download/dam-catalog/' + damCatalogId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        FileService.saveAs(response, 'download.zip');
      });
  }

}
